import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, Download, Briefcase, Mail, Calendar, 
  TrendingUp, Clock, Building2, Loader2, RefreshCw, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';

import { apiUrl, API_URL } from '../utils/config';

const AnalyticsDashboard = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({
    contacts: [],
    demoBookings: [],
    resourceDownloads: [],
    careerApplications: []
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuth) {
      fetchAllData();
    }
  }, [isAuth]);

  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        fetch(apiUrl("contacts")),
        fetch(apiUrl("demo-bookings")),
        fetch(apiUrl("resource-downloads")),
        fetch(apiUrl("career-applications"))
      ]);
      
      const parseResult = async (result) => {
        if (result.status === 'fulfilled' && result.value.ok) {
          return await result.value.json();
        }
        return [];
      };
      
      const [contacts, demos, downloads, applications] = await Promise.all(
        results.map(parseResult)
      );
      
      setData({
        contacts: contacts || [],
        demoBookings: demos || [],
        resourceDownloads: downloads || [],
        careerApplications: applications || []
      });
    } catch (err) {
      setError(err.message || 'Failed to load analytics data');
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecentItems = (items, days = 7) => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return items.filter(item => {
      const date = new Date(item.timestamp || item.created_at || item.submittedAt);
      return date >= cutoff;
    });
  };

  const stats = [
    {
      label: 'Contact Submissions',
      value: data.contacts.length,
      recent: getRecentItems(data.contacts).length,
      icon: Mail,
      color: 'blue',
      tab: 'contacts'
    },
    {
      label: 'Demo Bookings',
      value: data.demoBookings.length,
      recent: getRecentItems(data.demoBookings).length,
      icon: Calendar,
      color: 'green',
      tab: 'demos'
    },
    {
      label: 'Resource Downloads',
      value: data.resourceDownloads.length,
      recent: getRecentItems(data.resourceDownloads).length,
      icon: Download,
      color: 'purple',
      tab: 'downloads'
    },
    {
      label: 'Career Applications',
      value: data.careerApplications.length,
      recent: getRecentItems(data.careerApplications).length,
      icon: Briefcase,
      color: 'orange',
      tab: 'applications'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Analytics Dashboard" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader
        title="Analytics Dashboard"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        showAddButton={false}
      />

      <div className="container mx-auto px-6 py-8">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-800">Failed to load data</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
            <Button
              onClick={fetchAllData}
              className="bg-red-600 hover:bg-red-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Refresh Button */}
        <div className="flex justify-end mb-6">
          <Button
            onClick={fetchAllData}
            variant="outline"
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <button
                key={`analytics-${index}`}
                onClick={() => setActiveTab(stat.tab)}
                className={`bg-white rounded-xl shadow-md p-6 text-left hover:shadow-lg transition-all border-2 ${
                  activeTab === stat.tab ? 'border-blue-500' : 'border-transparent'
                }`}
                data-testid={`stat-card-${stat.tab}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {stat.recent > 0 && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      +{stat.recent} this week
                    </span>
                  )}
                </div>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </button>
            );
          })}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-blue-600' : ''}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </Button>
          <Button
            variant={activeTab === 'contacts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contacts')}
            className={activeTab === 'contacts' ? 'bg-blue-600' : ''}
          >
            <Mail className="w-4 h-4 mr-2" />
            Contacts ({data.contacts.length})
          </Button>
          <Button
            variant={activeTab === 'demos' ? 'default' : 'outline'}
            onClick={() => setActiveTab('demos')}
            className={activeTab === 'demos' ? 'bg-blue-600' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Demo Bookings ({data.demoBookings.length})
          </Button>
          <Button
            variant={activeTab === 'downloads' ? 'default' : 'outline'}
            onClick={() => setActiveTab('downloads')}
            className={activeTab === 'downloads' ? 'bg-blue-600' : ''}
          >
            <Download className="w-4 h-4 mr-2" />
            Downloads ({data.resourceDownloads.length})
          </Button>
          <Button
            variant={activeTab === 'applications' ? 'default' : 'outline'}
            onClick={() => setActiveTab('applications')}
            className={activeTab === 'applications' ? 'bg-blue-600' : ''}
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Applications ({data.careerApplications.length})
          </Button>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h3>
                <div className="space-y-4">
                  {[...data.contacts.slice(0, 3).map(c => ({ ...c, type: 'contact' })),
                    ...data.demoBookings.slice(0, 3).map(d => ({ ...d, type: 'demo' })),
                    ...data.resourceDownloads.slice(0, 3).map(r => ({ ...r, type: 'download' })),
                    ...data.careerApplications.slice(0, 3).map(a => ({ ...a, type: 'application' }))]
                    .sort((a, b) => new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at))
                    .slice(0, 10)
                    .map((item, index) => (
                      <div key={`contact-${index}`} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${
                          item.type === 'contact' ? 'bg-blue-100 text-blue-600' :
                          item.type === 'demo' ? 'bg-green-100 text-green-600' :
                          item.type === 'download' ? 'bg-purple-100 text-purple-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          {item.type === 'contact' && <Mail className="w-4 h-4" />}
                          {item.type === 'demo' && <Calendar className="w-4 h-4" />}
                          {item.type === 'download' && <Download className="w-4 h-4" />}
                          {item.type === 'application' && <Briefcase className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {item.fullName || item.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.type === 'contact' && `Contact inquiry from ${item.company || 'N/A'}`}
                            {item.type === 'demo' && `Demo booking - ${item.company || 'N/A'}`}
                            {item.type === 'download' && `Downloaded: ${item.resourceTitle || item.resource_title || 'Resource'}`}
                            {item.type === 'application' && `Applied for: ${item.positionTitle || 'Position'}`}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formatDate(item.timestamp || item.created_at)}
                        </span>
                      </div>
                    ))}
                  {data.contacts.length === 0 && data.demoBookings.length === 0 && 
                   data.resourceDownloads.length === 0 && data.careerApplications.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No activity yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Email</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Company</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Interest</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                      <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.contacts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-gray-500">
                          No contact submissions yet
                        </td>
                      </tr>
                    ) : (
                      data.contacts.map((contact, index) => (
                        <tr key={`row-${index}`} className="hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-900">{contact.fullName}</td>
                          <td className="py-4 px-6">
                            <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                              {contact.email}
                            </a>
                          </td>
                          <td className="py-4 px-6 text-gray-600">{contact.company || 'N/A'}</td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                              {contact.interest || 'General'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-gray-500 text-sm">
                            {formatDate(contact.timestamp || contact.created_at)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailModal({ type: 'contact', data: contact })}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Demo Bookings Tab */}
            {activeTab === 'demos' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Email</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Company</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Size</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                      <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.demoBookings.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-gray-500">
                          No demo bookings yet
                        </td>
                      </tr>
                    ) : (
                      data.demoBookings.map((booking, index) => (
                        <tr key={`row-${index}`} className="hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-900">{booking.fullName}</td>
                          <td className="py-4 px-6">
                            <a href={`mailto:${booking.email}`} className="text-blue-600 hover:underline">
                              {booking.email}
                            </a>
                          </td>
                          <td className="py-4 px-6 text-gray-600">{booking.company || 'N/A'}</td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              {booking.companySize || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-gray-500 text-sm">
                            {formatDate(booking.timestamp || booking.created_at)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailModal({ type: 'demo', data: booking })}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Resource Downloads Tab */}
            {activeTab === 'downloads' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Email</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Company</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Resource</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.resourceDownloads.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-gray-500">
                          No resource downloads yet
                        </td>
                      </tr>
                    ) : (
                      data.resourceDownloads.map((download, index) => (
                        <tr key={`row-${index}`} className="hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-900">{download.name || download.fullName}</td>
                          <td className="py-4 px-6">
                            <a href={`mailto:${download.email}`} className="text-blue-600 hover:underline">
                              {download.email}
                            </a>
                          </td>
                          <td className="py-4 px-6 text-gray-600">{download.company || 'N/A'}</td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                              {download.resourceTitle || download.resource_title || 'Resource'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-gray-500 text-sm">
                            {formatDate(download.timestamp || download.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Career Applications Tab */}
            {activeTab === 'applications' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Name</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Email</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Position</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">LinkedIn</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-900">Date</th>
                      <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.careerApplications.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-gray-500">
                          No career applications yet
                        </td>
                      </tr>
                    ) : (
                      data.careerApplications.map((application, index) => (
                        <tr key={`row-${index}`} className="hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-900">{application.fullName}</td>
                          <td className="py-4 px-6">
                            <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline">
                              {application.email}
                            </a>
                          </td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                              {application.positionTitle || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            {application.linkedin ? (
                              <a href={application.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Profile
                              </a>
                            ) : 'N/A'}
                          </td>
                          <td className="py-4 px-6 text-gray-500 text-sm">
                            {formatDate(application.timestamp || application.created_at)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailModal({ type: 'application', data: application })}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {detailModal.type === 'contact' && 'Contact Details'}
                {detailModal.type === 'demo' && 'Demo Booking Details'}
                {detailModal.type === 'application' && 'Application Details'}
              </h3>
              <button
                onClick={() => setDetailModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              {Object.entries(detailModal.data).map(([key, value]) => {
                if (key === 'id' || key === '_id') return null;
                return (
                  <div key={key} className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                    </span>
                    <span className="text-gray-900 mt-1">
                      {typeof value === 'object' ? JSON.stringify(value) : (value || 'N/A')}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="p-6 border-t border-gray-200">
              <Button onClick={() => setDetailModal(null)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
